import React, { ReactElement, useContext } from 'react';
import { Input as AntInput } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { ValidationContext } from './Validation';
import { Skeleton } from './Skeleton';

type InputValueType = string | number;
type InputType = 'text' | 'number' | 'password';
type InputSizeType = 'large' | 'middle' | 'small';

interface CustomStyles {
  width?: CSSSize | 0;
  placeholderSize?: CSSSize;
  fontSize?: CSSSize;
}

interface InputProps<Value extends InputValueType> {
  value?: Value;
  onChange?: (value: Value) => void;
  onPressEnter?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: InputType;
  showPasswordVisibilityToggle?: boolean;
  addonAfter?: React.ReactNode;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  size?: InputSizeType;
  showCount?: () => React.ReactNode;
  loading?: boolean;
  customStyles?: CustomStyles;
  numberCanStartWithZero?: boolean;
  autoFocus?: boolean;
  testId?: string;
}

function Input<Value extends InputValueType>({
  value,
  onChange,
  onPressEnter,
  type,
  size,
  placeholder,
  showPasswordVisibilityToggle,
  addonAfter,
  suffix,
  prefix,
  disabled,
  className,
  maxLength,
  loading,
  showCount,
  autoFocus,
  numberCanStartWithZero,
  customStyles,
  onBlur,
  testId,
}: InputProps<Value>): ReactElement {
  const { validateStatus } = useContext(ValidationContext);
  const antOnChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    onChange && onChange(e.target.value as Value);
  };

  if (loading) {
    return (
      <SkeletonWrapper>
        <StyledSkeleton className={className} />
      </SkeletonWrapper>
    );
  }

  if (type === 'password') {
    return (
      <StyledPasswordInput
        autoFocus={autoFocus}
        status={validateStatus}
        visibilityToggle={showPasswordVisibilityToggle}
        value={value}
        onChange={antOnChange}
        onPressEnter={() => onPressEnter && onPressEnter()}
        placeholder={placeholder}
        addonAfter={addonAfter}
        suffix={suffix}
        prefix={prefix}
        count={{
          show: showCount,
        }}
        disabled={disabled}
        size={size ? size : 'large'}
        className={className}
        customstyles={customStyles}
        maxLength={maxLength}
        data-testid={testId}
      />
    );
  } else if (type === 'number') {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let { value: inputValue } = e.target;

      if (!numberCanStartWithZero) {
        inputValue = inputValue.replace(/\b0+/g, '');
      }
      const reg = /^\d+$/;

      if (reg.test(inputValue) || inputValue === '') {
        onChange && onChange(inputValue as Value);
      }
    };

    return (
      <StyledInput
        autoFocus={autoFocus}
        addonAfter={addonAfter}
        suffix={suffix}
        prefix={prefix}
        customstyles={customStyles}
        type={'text'}
        count={{
          show: showCount,
        }}
        value={value}
        status={validateStatus}
        size={size ? size : 'large'}
        onChange={handleChange}
        onPressEnter={() => onPressEnter && onPressEnter()}
        onBlur={() => onBlur?.()}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        maxLength={maxLength}
        data-testid={testId}
      />
    );
  } else {
    return (
      <StyledInput
        autoFocus={autoFocus}
        addonAfter={addonAfter}
        suffix={suffix}
        prefix={prefix}
        type={type}
        count={{
          show: showCount,
        }}
        value={value}
        customstyles={customStyles}
        status={validateStatus}
        size={size ? size : 'large'}
        onChange={antOnChange}
        onPressEnter={() => onPressEnter && onPressEnter()}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        maxLength={maxLength}
        onBlur={() => onBlur?.()}
        data-testid={testId}
      />
    );
  }
}

const StyledInput = styled(AntInput)<{ customstyles?: CustomStyles }>`
  &.ant-input {
    width: ${props => props.customstyles?.width || '100%'};
    font-size: ${props => props.customstyles?.fontSize};
  }

  &.ant-input::placeholder {
    font-size: ${props => props.customstyles?.placeholderSize};
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  &[type='number'] {
    -moz-appearance: textfield; /* Firefox */
  }
`;
const StyledPasswordInput = styled(AntInput.Password)<{ customstyles?: CustomStyles }>`
  &.ant-input {
    width: ${props => props.customstyles?.width};
  }
`;
const SkeletonWrapper = styled.div`
  height: 40px;
`;
const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    height: 40px;
    vertical-align: middle;
    border-radius: 8px;
  }
`;

export default Input;
